import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { TenantIdentityCard } from '@/components/TenantIdentity'
import { setAdmin } from '@/utils/adminAuth'
import { loginAdmin } from '@/api/adminApi'
import {
  getClientPublicEntryPath,
  getTenantEntryPath,
  getTenantSlug,
  hasTenantBound,
  setTenantSlug,
} from '@/config/tenant'
import { useTenant } from '@/contexts/TenantContext'
import { consumeAuthNotice } from '@/utils/authFlow'
import { useSaveFeedback } from '@/hooks/useSaveFeedback'
import styles from './Admin.module.css'

export function AdminLoginPage() {
  const navigate = useNavigate()
  const { setCachedNome, nome: empresaNome } = useTenant()
  const tenantLocked = hasTenantBound()
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [tenantSlug, setTenantSlugField] = useState(() => getTenantSlug())
  const [erro, setErro] = useState('')
  const [loading, setLoading] = useState(false)
  const { notifySaved, notifyError, notifyValidation } = useSaveFeedback()

  useEffect(() => {
    const msg = consumeAuthNotice('admin')
    if (msg) setErro(msg)
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErro('')
    const emailTrim = email.trim()
    const senhaTrim = senha.trim()
    if (!emailTrim || !senhaTrim) {
      const msg = 'Preencha e-mail e senha.'
      setErro(msg)
      notifyValidation(msg)
      return
    }
    const slugTrim = (tenantLocked ? getTenantSlug() : tenantSlug).trim().toLowerCase()
    if (!slugTrim) {
      const msg = 'Abra primeiro o link da sua empresa (formato /e/nome-da-empresa).'
      setErro(msg)
      notifyValidation(msg)
      return
    }
    setTenantSlug(slugTrim)
    setLoading(true)
    try {
      const { email: adminEmail, tenantSlug: slug, tenantNome } = await loginAdmin(
        emailTrim,
        senhaTrim,
        slugTrim
      )
      setAdmin(null, adminEmail, {
        tenantSlug: slug ?? slugTrim,
        tenantNome: tenantNome ?? slugTrim,
      })
      if (tenantNome) setCachedNome(tenantNome)
      notifySaved('Sessão de administrador iniciada.')
      navigate('/admin/dashboard', { replace: true })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'E-mail ou senha incorretos. Tente novamente.'
      setErro(msg)
      notifyError(err, msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className={styles.loginPage}>
      <div className={styles.loginCard}>
        <h1 className={styles.loginTitle}>
          {tenantLocked ? `Painel — ${empresaNome}` : 'Acesso administrador'}
        </h1>
        <p className={styles.loginSubtitle}>
          {tenantLocked
            ? 'Entre com o e-mail e a senha de administrador desta empresa.'
            : 'Abra o link que a plataforma enviou (/e/nome-da-empresa) ou indique o identificador abaixo.'}
        </p>
        {erro && <p className={styles.erro} role="alert">{erro}</p>}
        <form className={styles.loginForm} onSubmit={handleSubmit}>
          {!tenantLocked && (
            <>
              <label className={styles.label} htmlFor="admin-tenant">
                Identificador da empresa
              </label>
              <input
                id="admin-tenant"
                type="text"
                className={styles.input}
                placeholder="ex.: meu-salao"
                value={tenantSlug}
                onChange={(e) => setTenantSlugField(e.target.value)}
                onBlur={() => {
                  const s = tenantSlug.trim().toLowerCase()
                  if (s) setTenantSlug(s)
                }}
                autoComplete="off"
                spellCheck={false}
              />
              <p className={styles.loginHint}>
                Só necessário se ainda não abriu o link oficial da empresa.
              </p>
            </>
          )}
          {tenantLocked && <TenantIdentityCard compact />}
          <label className={styles.label} htmlFor="admin-email">
            E-mail
          </label>
          <input
            id="admin-email"
            type="email"
            className={styles.input}
            placeholder="admin@salao.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
          />
          <label className={styles.label} htmlFor="admin-senha">
            Senha
          </label>
          <input
            id="admin-senha"
            type="password"
            className={styles.input}
            placeholder="••••••••"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            autoComplete="current-password"
          />
          <button type="submit" className={styles.btnEntrar} disabled={loading}>
            {loading ? 'A verificar…' : 'Entrar'}
          </button>
        </form>
        <div className={styles.loginFooterLinks}>
          {tenantLocked && (
            <Link to={getTenantEntryPath(getTenantSlug())} className={styles.linkVoltar}>
              ← Portal da empresa
            </Link>
          )}
          <button
            type="button"
            className={styles.linkVoltar}
            onClick={() => navigate(tenantLocked ? getClientPublicEntryPath() : '/')}
          >
            App do cliente
          </button>
        </div>
      </div>
    </main>
  )
}
